import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { MapboxCustomLayerService } from '@/services/3d'
import { useEventPlannerStore } from '../store/eventPlannerStore'
import type { MovementPlan } from '../motion/movementPlan'
import { bakeAllTracks, makePose, sampleActorInto } from '../motion/bakedTrack'
import { registerMotionApply } from '../motion/motionDriver'

/**
 * Apply each actor's `pose(t)` to its scene object. The pose is a pure function
 * of time, so this same applier serves both playback and scrubbing.
 *
 * Two drivers feed it:
 *  - while PLAYING, `usePlaybackLoop` calls `applyAt(liveClock)` every animation
 *    frame via the motion-driver bus (imperative, no React re-render per frame),
 *  - while PAUSED / SCRUBBING (and on the initial mount or a plan change), the
 *    effect below calls `applyAt(currentTime)` on each store change.
 *
 * Performance: plans are baked once into time-sorted tracks with precomputed
 * arc-length tables (`bakeAllTracks`); each frame samples with a monotonic
 * cursor into a reused pose + scratch position array (zero allocation). A
 * quantized last-applied cache (0.1ft / 1°, matching the persisted precision)
 * skips redundant `setAircraftTransform` calls, so only actors that actually
 * moved are written. One `markSceneDirty()` per pass.
 *
 * Membership (which objects exist) is owned by `useEventSceneSync`; this hook
 * only moves objects already in the scene. If an object isn't loaded yet
 * (`setAircraftTransform` → false) the next tick retries.
 */
export function useEventActorMotion(
  layerService: MapboxCustomLayerService | null,
  plans: Record<string, MovementPlan>
) {
  const currentTime = useEventPlannerStore((s) => s.currentTime)
  const playback = useEventPlannerStore((s) => s.playback)
  const enableAnimation = useEventPlannerStore((s) => s.motionConfig.enableAnimation)
  const stageEpoch = useEventPlannerStore((s) => s.stageEpoch)

  const tracks = useMemo(() => bakeAllTracks(plans), [plans])

  // Reused per-frame scratch — never reallocated.
  const pose = useRef(makePose())
  const xyz = useRef<[number, number, number]>([0, 0, 0])
  // actorId → last applied [qx, qy, qz, qRot] (quantized; 0.1ft / 1°).
  const applied = useRef(new Map<string, Int32Array>())

  const applyAt = useCallback(
    (t: number) => {
      if (!layerService || !enableAnimation) return
      const p = pose.current
      const pos = xyz.current
      const cache = applied.current
      let dirty = false

      for (const track of tracks) {
        for (const actor of track.actors) {
          sampleActorInto(actor, t, p)
          if (!p.present) {
            // Absent at t (e.g. tug between tows) — forget so a re-entry re-applies.
            cache.delete(actor.actorId)
            continue
          }
          const qx = Math.round(p.x * 10)
          const qy = Math.round(p.y * 10)
          const qz = Math.round(p.z * 10)
          const qr = Math.round(p.rotationDeg)
          const prev = cache.get(actor.actorId)
          if (prev && prev[0] === qx && prev[1] === qy && prev[2] === qz && prev[3] === qr) continue

          pos[0] = p.x
          pos[1] = p.y
          pos[2] = p.z
          const ok = layerService.setAircraftTransform(actor.actorId, pos, p.rotationDeg)
          if (ok) {
            if (prev) {
              prev[0] = qx
              prev[1] = qy
              prev[2] = qz
              prev[3] = qr
            } else {
              cache.set(actor.actorId, Int32Array.of(qx, qy, qz, qr))
            }
            dirty = true
          }
        }
      }

      if (dirty) layerService.markSceneDirty()
    },
    [layerService, enableAnimation, tracks]
  )

  // A stage reset disposes every object and rebuilds it, so the quantized
  // last-applied cache describes objects that no longer exist — keeping it would
  // suppress the first transform write on any actor whose pose didn't change.
  useEffect(() => {
    applied.current.clear()
  }, [stageEpoch])

  // Let the rAF playback loop drive the applier at the display refresh rate.
  useEffect(() => {
    registerMotionApply(applyAt)
    return () => registerMotionApply(null)
  }, [applyAt])

  // Drive directly on store changes when NOT playing (scrub / seek / pause / mount)
  // and whenever the baked tracks change. During playback the rAF loop drives.
  useEffect(() => {
    if (playback !== 'playing') applyAt(currentTime)
  }, [applyAt, currentTime, playback])
}
